import { AbsenceType } from '~/data/absences/types';
import { fetchFromOldApi } from '~/services/Http';

export * from './useAbsenceDaysCalculation';
export * from './useGetEmployeeAbsenceData';

export enum AbsenceStatus {
  DRAFT = 'DRAFT',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  PROPOSED = 'PROPOSED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export type AbsenceAttachment = {
  fAttachmentId: number;
  fAttachmentName: string;
  fAttachmentType?: string;
  fAttachmentIsArchived?: boolean;
};

export interface ShortAbsence {
  fAbsenceShortUId?: string;
  fAbsenceShortAttachment: AbsenceAttachment;
  fAbsenceShortBalanceYear?: number;
  fAbsenceShortDays?: number;
  fAbsenceShortEndDate?: string;
  fAbsenceShortAbsenceHours?: number;
  fAbsenceShortPlannedWorkingHours?: number;
  fAbsenceShortInfo?: string;
  fAbsenceShortReason?: string;
  fAbsenceShortStartDate: string;
  fAbsenceShortStatus: AbsenceStatus;
  fAbsenceShortDeciderEmpId?: number;
  fAbsenceShortDeciderName?: string;
  fAbsenceShortDecidedDate?: string;
  fAbsenceShortEmpId?: number;
  fAbsenceShortId?: string;
  fAbsenceShortEnteredUserName?: string;
  fAbsenceShortEnteredTime?: string;
  fAbsenceShortSupersederName?: string;
  fAbsenceShortSupersededReason?: string;
  fAbsenceShortSupersededTime?: string;
}

export interface LongAbsence {
  fAbsenceLongUId?: string;
  fAbsenceLongAttachment: AbsenceAttachment;
  fAbsenceLongEndDate?: string;
  fAbsenceLongInfo?: string;
  fAbsenceLongReason?: string;
  fAbsenceLongStartDate: string;
  fAbsenceLongStatus: AbsenceStatus;
  fAbsenceLongDeciderEmpId?: number;
  fAbsenceLongDeciderName?: string;
  fAbsenceLongDecidedDate?: string;
  fAbsenceLongEmpId?: number;
  fAbsenceLongId?: string;
  fAbsenceLongEnteredUserName?: string;
  fAbsenceLongEnteredTime?: string;
  fAbsenceLongSupersederName?: string;
  fAbsenceLongSupersededReason?: string;
  fAbsenceLongSupersededTime?: string;
}

export type ProjectedBalance = {
  fProjectedApproved: number;
  fProjectedUnapproved: number;
  fProjectedCalcRule: number;
  fProjectedExtendable: boolean;
};

export type YearlyVacationBalance = {
  fHolidayCode: string;
  fHolidayYear: number;
  fHolidayBalance: number;
  fHolidayApproved: number;
  fHolidayUnapproved: number;
  fHolidayCalcRule: number;
  fHolidayExtendable: boolean;
};

export interface VacationBalances {
  fVacationBalance: number;
  fVacationBalanceProjDed: ProjectedBalance;
  fVacationBalanceBank1: number;
  fVacationBalanceBank1ProjDed: ProjectedBalance;
  fVacationBalanceBank2: number;
  fVacationBalanceBank2ProjDed: ProjectedBalance;
  fVacationBalanceBank3: number;
  fVacationBalanceBank3ProjDed: ProjectedBalance;
  fVacationBalanceExtra?: number;
  fVacationBalanceExtraProjDed: ProjectedBalance;
  fYearlyVacationBalances: YearlyVacationBalance[];
}
export interface Absence {
  absenceLongs: LongAbsence[];
  absenceShorts: ShortAbsence[];
  vacationBalance: VacationBalances;
}

export enum AbsenceLimitedType {
  VACATION = 'VACATION',
  OTHER = 'OTHER',
}

export interface AbsenceClassification {
  code: string;
  description: string;
  editableByEmp: boolean;
  name: string;
  seq: number;
}

export interface AbsenceLimitedGeneral {
  fAbsenceLimitedId?: string;
  fAbsenceLimitedUId?: string;
  fAbsenceLimitedEmpId?: number;
  fAbsenceLimitedStartDate: string;
  fAbsenceLimitedEndDate?: string;
  fAbsenceLimitedStatus: AbsenceStatus;
  fAbsenceLimitedClassification: AbsenceLimitedType;
  fAbsenceLimitedDays?: number;
  fAbsenceLimitedSupersededName?: string;
  fAbsenceLimitedSupersededReason?: string;
  fAbsenceLimitedSupersededTime?: string;
}

export interface AbsenceLimited {
  absenceLongs: AbsenceLimitedGeneral[];
  absenceShorts: AbsenceLimitedGeneral[];
}

export const GET_SHORT_ABSENCES_SWR_KEY = 'getShortAbsencess';
export const getShortAbsences = async (
  employeeId: number,
  period?: 'historical',
): Promise<ShortAbsence[]> => {
  const getQueryParams = () => {
    let params = [];

    if (!!period) {
      params.push(`period=${period}`);
    }

    return params.length > 0 ? `?${params.join('&')}` : '';
  };

  return fetchFromOldApi(
    `/json/employee/${employeeId}/absenceshort${getQueryParams()}`,
  ).then((r) => r.json());
};

export const GET_SHORT_ABSENCE_HISTORY_SWR_KEY = 'getShortAbsenceHistory';
export const getShortAbsenceHistory = (
  employeeId: number,
  absenceId: string,
): Promise<ShortAbsence[]> =>
  fetchFromOldApi(
    `/json/employee/${employeeId}/absenceshort/${absenceId}`,
  ).then((r) => r.json());

export const GET_LONG_ABSENCES_SWR_KEY = 'getLongAbsences';
export const getLongAbsences = async (
  employeeId: number,
  period?: 'historical',
): Promise<LongAbsence[]> => {
  const getQueryParams = () => {
    let params = [];

    if (!!period) {
      params.push(`period=${period}`);
    }

    return params.length > 0 ? `?${params.join('&')}` : '';
  };

  return fetchFromOldApi(
    `/json/employee/${employeeId}/absencelong${getQueryParams()}`,
  ).then((r) => r.json());
};

export const GET_LONG_ABSENCE_HISTORY_SWR_KEY = 'getLongAbsenceHistory';
export const getLongAbsenceHistory = (
  employeeId: number,
  absenceId: string,
): Promise<LongAbsence[]> =>
  fetchFromOldApi(`/json/employee/${employeeId}/absencelong/${absenceId}`).then(
    (r) => r.json(),
  );

export const GET_USER_ABSENCE_SWR_KEY = 'getUserAbsence';
export const getUserAbsence = (employeeId: number): Promise<Absence> =>
  fetchFromOldApi(`/json/employee/${employeeId}/absence`).then((r) => r.json());

export const GET_USER_ABSENCE_LIMITED_SWR_KEY = 'getUserAbsenceLimited';
export const getUserAbsenceLimited = (
  employeeId: number,
): Promise<AbsenceLimited> =>
  fetchFromOldApi(`/json/employee/${employeeId}/absencelimited`).then((r) =>
    r.json(),
  );

const submitEmployeeAbsence = async <
  TAbsence extends ShortAbsence | LongAbsence,
>(props: {
  employeeId: number;
  absenceType: AbsenceType;
  absenceId?: string;
  data: TAbsence | FormData;
}): Promise<TAbsence> => {
  const { employeeId, absenceType, absenceId, data } = props;

  const submitAsFormData = data instanceof FormData;

  const url = `/${submitAsFormData ? 'form' : 'json'}/employee/${employeeId}/${
    absenceType === AbsenceType.LONG ? 'absencelong' : 'absenceshort'
  }${!!absenceId ? `/${absenceId}` : ''}`;

  return fetchFromOldApi(url, {
    method: !!absenceId ? 'PUT' : 'POST',
    ...(!submitAsFormData && {
      headers: { 'Content-Type': 'application/json' },
    }),
    body: submitAsFormData ? data : JSON.stringify(data),
  }).then((r) => r.json());
};

export const submitShortAbsence = (props: {
  employeeId: number;
  absenceId?: string;
  data: ShortAbsence | FormData;
}) =>
  submitEmployeeAbsence<ShortAbsence>({
    ...props,
    absenceType: AbsenceType.SHORT,
  });

export const submitLongAbsence = (props: {
  employeeId: number;
  absenceId?: string;
  data: LongAbsence | FormData;
}) =>
  submitEmployeeAbsence<LongAbsence>({
    ...props,
    absenceType: AbsenceType.LONG,
  });

export const createShortAbsence = (
  employeeId: number,
  absence: ShortAbsence,
): Promise<ShortAbsence> =>
  fetchFromOldApi(`/form/employee/${employeeId}/absenceshort`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(absence),
  }).then((r) => r.json());

export const createShortAbsenceFormData = async (
  employeeId: number,
  fd: FormData,
) => {
  await fetchFromOldApi(`/form/employee/${employeeId}/absenceshort`, {
    method: 'POST',
    body: fd,
  });
};
export const createLongAbsence = (
  employeeId: number,
  absence: LongAbsence,
): Promise<LongAbsence> =>
  fetchFromOldApi(`/form/employee/${employeeId}/absencelong`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(absence),
  }).then((r) => r.json());

export const createLongAbsenceFormData = async (
  employeeId: number,
  fd: FormData,
) => {
  await fetchFromOldApi(`/form/employee/${employeeId}/absencelong`, {
    method: 'POST',
    body: fd,
  });
};

export const approveShortAbsence = (
  employeeId: number,
  absence: ShortAbsence,
): Promise<ShortAbsence> =>
  fetchFromOldApi(
    `/json/employee/${employeeId}/absenceshort/${absence.fAbsenceShortUId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(absence),
    },
  ).then((r) => r.json());

export const approveLongAbsence = (
  employeeId: number,
  absence: LongAbsence,
): Promise<LongAbsence> =>
  fetchFromOldApi(
    `/json/employee/${employeeId}/absencelong/${absence.fAbsenceLongUId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(absence),
    },
  ).then((r) => r.json());

export const deleteShortAbsence = (
  employeeId: number,
  absenceId: string | undefined,
): Promise<void> =>
  fetchFromOldApi(`/json/employee/${employeeId}/absenceshort/${absenceId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  }).then((r) => r.json());

export const deleteLongAbsence = (
  employeeId: number,
  absenceId: string | undefined,
): Promise<void> =>
  fetchFromOldApi(`/json/employee/${employeeId}/absencelong/${absenceId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  }).then((r) => r.json());
